import Text from '@shared/components/content/text';
import Unit, { renderUnit, UnitVariant } from '@shared/components/content/unit';
import { Card } from '@shared/components/ui/card';
import { cn, formatNumber } from '@shared/lib/utils';

export interface ScopeBreakdownRow {
  label: string;
  /** CSS colour for the bar fill (e.g. `var(--chart-3)`). */
  barColor: string;
  tonnes: number | null;
  organisationsCount: number;
}

interface ScopeBreakdownCardProps {
  title: string;
  totalTonnes: number | null;
  unit?: UnitVariant;
  rows: ScopeBreakdownRow[];
  className?: string;
}

/**
 * Hero panel for the group emissions dashboards: an anchor total on the left
 * and one horizontal bar per scope on the right, each with its emissions and
 * reporting-organisation count. Bars are sized relative to the largest scope.
 */
export function ScopeBreakdownCard({
  title,
  totalTonnes,
  unit = 'tCO2e',
  rows,
  className,
}: ScopeBreakdownCardProps) {
  const maxTonnes = Math.max(1, ...rows.map((row) => row.tonnes ?? 0));

  return (
    <Card
      className={cn(
        'flex flex-col gap-6 p-6 md:flex-row md:items-center',
        className
      )}
    >
      <div className="flex flex-col gap-2 md:w-64 md:shrink-0">
        <Text className="text-sm font-semibold text-primary-700">{title}</Text>
        <div className="flex flex-wrap items-baseline gap-x-2">
          <Text className="text-4xl font-medium leading-none text-primary-800">
            {totalTonnes != null ? formatNumber(totalTonnes) : '-'}
          </Text>
          {totalTonnes != null && (
            <Unit
              variant={unit}
              className="text-sm font-medium text-primary-500"
            />
          )}
        </div>
      </div>

      <div className="hidden self-stretch border-l md:block" />

      <div className="flex flex-1 flex-col gap-3">
        {rows.map((row) => {
          return (
            <div
              key={row.label}
              className="grid grid-cols-[64px_1fr_auto] items-center gap-4"
            >
              <Text variant="subtle" className="text-gray-900">
                {row.label}
              </Text>
              <div className="h-2.5 overflow-hidden rounded-full bg-gray-50">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${((row.tonnes ?? 0) / maxTonnes) * 100}%`,
                    background: row.barColor,
                  }}
                />
              </div>
              <Text variant="subtle" className="whitespace-nowrap text-right">
                <span className="font-semibold text-gray-900">
                  {row.tonnes != null ? formatNumber(row.tonnes) : '-'}
                </span>{' '}
                {renderUnit(unit)} from{' '}
                <span className="font-semibold text-gray-900">
                  {row.organisationsCount}
                </span>{' '}
                orgs
              </Text>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
